import { FC, useRef } from 'react';
import { Category, Headline } from '~/lib/data-contract';
import { CardCategory } from '../CardCategory/CardCategory';
import { weakKey } from '~/shared/utils/jsx';
import { Button } from '~/shared/components';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron.svg';
import { useGetCarouselOptions } from '~/templates/blocks/hooks/useGetCarouselOptions';
import styles from './CardCarousel.module.scss';
import Typography from '~/shared/components/Typography/Typography';
import { Swiper, SwiperSlide } from 'swiper/react';
import useSwiperRef from '~/shared/components/Carousel/hooks/useSwiperRef';
import { Navigation } from 'swiper/modules';

export type CarouselProps = {
    items: Category[];
    priority?: boolean;
    headline?: Headline;
};

const CategoryCarousel: FC<CarouselProps> = (props: CarouselProps) => {
    const { items, priority, headline } = props;
    const { shouldShowButtons, desktop, tablet, carouselOptions, mobile } =
        useGetCarouselOptions(items);
    const prevRef = useRef<HTMLButtonElement>(null);
    const nextRef = useRef<HTMLButtonElement>(null);
    const prev = useSwiperRef({ ref: prevRef });
    const next = useSwiperRef({ ref: nextRef });

    return (
        <div
            className={styles.cardCarouselContainer}
            style={
                {
                    ['--slide-per-view-desktop']: desktop.slidePerView,
                    ['--slide-per-view-tablet']: tablet.slidePerView,
                    ['--slide-per-view-mobile']: mobile.slidePerView,
                    ['--swiper-wrapper-wrap']:
                        tablet.numberOfRows > 1 || mobile.numberOfRows > 1 ? 'wrap' : 'nowrap',
                } as React.CSSProperties
            }
        >
            {headline?.text && (
                <div className={styles.cardCarouselheadline}>
                    <Typography
                        className={styles.cardCarouselheadlineText}
                        variant={'title-S'}
                        as="h2"
                        data-testid="StyledHeadline"
                    >
                        <span>{headline.text}</span>
                    </Typography>
                </div>
            )}
            {shouldShowButtons && (
                <Button
                    className={styles.cardCarouselLeft}
                    variant="icon"
                    aria-label="Scroll til vesntre"
                    ref={prevRef}
                >
                    <ChevronLeft />
                </Button>
            )}
            <Swiper
                {...carouselOptions}
                modules={[Navigation]}
                navigation={{
                    prevEl: prev.element,
                    nextEl: next.element,
                }}
                className={styles.swiper}
            >
                {items.map((item, index) => (
                    <SwiperSlide className={styles.swiperSlide} key={index}>
                        <CardCategory
                            desktopAspectRatio={desktop.aspectRatio}
                            tabletAspectRatio={tablet.aspectRatio}
                            noPadding={true}
                            priority={priority}
                            key={weakKey(item)}
                            item={item}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
            {shouldShowButtons && (
                <Button
                    className={styles.cardCarouselRight}
                    variant="icon"
                    aria-label="Scroll til højre"
                    ref={nextRef}
                >
                    <ChevronRight />
                </Button>
            )}
        </div>
    );
};

export default CategoryCarousel;
